import React, { useMemo } from 'react'
import { VStack, Box, Button, Text, Icon, Checkbox } from '@chakra-ui/react'
import { LuPenLine } from 'react-icons/lu'
import { DonationContinueSection } from '../DonationContinueSection'
import { useFormContext } from '../context/FormContext'
import { setCoverTheCost, setPaymentMethod, nextStep, prevStep, setUseNewPaymentMethod } from '../context/formActions'
import { canContinueFromStep } from '../validation'
import { PaymentStep } from './PaymentStep'
import { formatMonthDayOrdinal, isPromise, type DonationResponse } from '@kuow/core'
import { useDonationFormUi } from '../context/UiContext'

function formatPaymentMethodDescription(subscription: DonationResponse): string {
  const method = subscription.payment_method
  if (!method) return 'your existing payment method'
  if (method.account_type === 'paypal') return 'PayPal'
  if (method.account_type === 'bank_account') {
    return method.last_digits ? `Bank account *${method.last_digits}` : 'Bank account'
  }
  if (method.account_type === 'credit_card') {
    return method.last_digits ? `Card *${method.last_digits}` : 'Card'
  }
  if (method.account_type === 'bill_later') return isPromise(method.account_type, method.delivery_method) ? 'Promise' : 'Bill later'
  return 'your existing payment method'
}

export const UpgradePaymentStep: React.FC = () => {
  const { state, dispatch } = useFormContext()
  const { showStepNavigator } = useDonationFormUi()
  const { activeSubscription, useNewPaymentMethod, formData, validation } = state

  if (useNewPaymentMethod) {
    return <PaymentStep />
  }

  if (!activeSubscription) {
    return null
  }

  const nextBillDate = useMemo(
    () => formatMonthDayOrdinal(activeSubscription.next_bill_date ?? activeSubscription.bill_date),
    [activeSubscription.next_bill_date, activeSubscription.bill_date],
  )

  const paymentMethodDescription = useMemo(
    () => formatPaymentMethodDescription(activeSubscription),
    [activeSubscription],
  )

  const currentAmount = formData.totalDonationAmount ?? formData.amount ?? formData.customAmount ?? 0
  const formattedCoverCostAmount = `$${formData.coverTheCostAmount.toFixed(2)}`
  const showBackButton = !showStepNavigator

  const handleChangePaymentMethod = () => {
    dispatch(setUseNewPaymentMethod(true))
    dispatch(setPaymentMethod(''))
  }

  const handleCoverTheCostChange = (checked: boolean) => {
    dispatch(setCoverTheCost(checked))
  }

  const handleContinue = () => {
    dispatch(setPaymentMethod('existing'))
    if (canContinueFromStep('payment', { ...validation, payment: true })) {
      dispatch(nextStep())
    }
  }

  return (
    <VStack gap={6} align="stretch" textAlign="center">
      <Box>
        <Text fontSize="md" fontWeight="semibold" mb={1.5}>
          Edit Existing Payment?
        </Text>
        <Text fontSize="xs" color="fg.muted" fontStyle="italic">
          Your next donation will be charged to <br />
          <Text as="span" fontWeight="bold">
            {paymentMethodDescription}
          </Text>
          {nextBillDate ? (
            <>
              {' '}
              on{' '}
              <Text as="span" fontWeight="bold">
                {nextBillDate}
              </Text>
              .
            </>
          ) : (
            '.'
          )}
          <br />
          Would you like to update it?
        </Text>
      </Box>

      <Button variant="outline" size="lg" onClick={handleChangePaymentMethod}>
        <Icon as={LuPenLine} mr={2} />
        Change Payment Method
      </Button>

      <Checkbox.Root
        checked={formData.coverTheCost}
        onCheckedChange={(details) => handleCoverTheCostChange(details.checked === true)}
      >
        <Checkbox.HiddenInput />
        <Checkbox.Control />
        <Checkbox.Label>
          <Text fontSize="xs" textAlign="left">
            I&apos;d like to help by covering the transaction fees of{' '}
            <Text as="span" fontWeight="bold">
              {formattedCoverCostAmount}
            </Text>{' '}
            for my donation.
          </Text>
        </Checkbox.Label>
      </Checkbox.Root>

      {/* frequency is guaranteed 'monthly' in monthly-upgrade mode via formReducer SET_FORM_DETAILS */}
      <DonationContinueSection
        amount={currentAmount}
        frequency={formData.frequency}
        buttonLabel="Continue"
        onClick={handleContinue}
        disabled={!canContinueFromStep('payment', { ...validation, payment: true })}
      />

      {showBackButton ? (
        <Button
          variant="subtle"
          size="xs"
          onClick={() => dispatch(prevStep())}
          alignSelf="center"
        >
          Back
        </Button>
      ) : null}
    </VStack>
  )
}


